'use client';

import { Lightbulb, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/shadcn/dialog';
import { Typography } from '@/shared/ui/shadcn/typography';
import type { ProjectGoalsGuide } from '@/types/project';

interface GoalsBriefGuideProps {
  guide: ProjectGoalsGuide['guide'];
}

/**
 * "How to write a great brief" callout card + modal guide shown on the project
 * goals page. All copy is server-driven (see useProjectGoalsGuide) — the app
 * analyzes the written task (not just the photo), so a rich brief materially
 * improves results. Mirrors the Apple/Android implementations.
 */
export function GoalsBriefGuide({ guide }: GoalsBriefGuideProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3 text-left transition-colors hover:bg-muted/70"
        >
          <Lightbulb className="h-5 w-5 shrink-0 text-primary" />
          <span className="flex-1 text-list-item-title">{guide.trigger}</span>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{guide.title}</DialogTitle>
        </DialogHeader>

        <Typography variant="muted" className="text-form-help">
          {guide.preamble}
        </Typography>

        <ul className="space-y-4">
          {guide.bullets.map((bullet, index) => (
            <li key={index} className="flex flex-col gap-0.5">
              <span className="text-list-item-title text-foreground">
                {bullet.title}
              </span>
              <Typography variant="muted" className="text-form-help">
                {bullet.body}
              </Typography>
            </li>
          ))}
        </ul>

        <Typography
          variant="muted"
          className="text-form-help font-medium text-foreground"
        >
          {guide.closing}
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
